import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiService } from "../../services/api";
import { LogoSVG } from "../Common/LogoSVG";
import { LangContext } from "../../contexts/LangContext";
import { useToasts } from "../../hooks/useToasts";
import { ToastCenter } from "../Common/ToastCenter";
import en from "../../i18n/en";
import pl from "../../i18n/pl";

export function ResetPasswordPage({ onGoToLogin }) {
  const { lang } = React.useContext(LangContext);
  const t = lang === "pl" ? pl : en;
  const [searchParams] = useSearchParams();
  const { toasts, add, remove } = useToasts();

  const [newPassword, setNewPassword] = useState("");
  const [newPassword2, setNewPassword2] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const token = searchParams.get("token");

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!newPassword || !newPassword2) {
      add(t.fillAllFields || "Please fill in all fields", "error");
      return;
    }

    if (newPassword !== newPassword2) {
      add(t.passwordsDontMatch, "error");
      return;
    }

    if (newPassword.length < 8) {
      add(t.passwordTooShort, "error");
      return;
    }

    if (!token) {
      add(t.invalidResetLink || "Invalid reset link", "error");
      return;
    }

    setLoading(true);
    try {
      await apiService.resetPassword(token, newPassword);
      setSuccess(true);
      add(t.passwordResetSuccess || "Password reset successfully!", "success");
    } catch (err) {
      add(err.message || t.passwordResetError || "Password reset failed", "error");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="auth-page">
        <div className="auth-box">
          <div className="auth-brand">
            <div className="auth-brand-icon">
              <LogoSVG size={26} />
            </div>
            <h1>{t.appName}</h1>
          </div>
          <div className="auth-card" style={{ textAlign: "center", padding: "40px 30px" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>❌</div>
            <h2 style={{ fontSize: 20, marginBottom: 8, color: "var(--dark)" }}>
              {t.invalidResetLink || "Invalid Reset Link"}
            </h2>
            <p style={{ color: "var(--muted)", fontSize: 14, marginBottom: 24 }}>
              {t.invalidResetLinkDesc || "This password reset link is invalid or has expired."}
            </p>
            <button className="btn-primary" onClick={onGoToLogin}>
              {t.goToLogin || "Go to Login"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <ToastCenter toasts={toasts} onRemove={remove} />
      <div className="auth-page">
        <div className="auth-box">
          <div className="auth-brand">
            <div className="auth-brand-icon">
              <LogoSVG size={26} />
            </div>
            <h1>{t.appName}</h1>
          </div>
          <div className="auth-card" style={{ padding: "30px" }}>
            {!success ? (
              <>
                <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
                  {t.resetPassword || "Reset Password"}
                </h3>
                <p style={{ fontSize: 13, color: "var(--muted)", marginBottom: 20 }}>
                  {t.resetPasswordDesc || "Enter your new password"}
                </p>
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label className="form-label">{t.newPassword || "New Password"}</label>
                    <div className="form-input-wrap">
                      <span className="form-input-icon">🔒</span>
                      <input
                        className="form-input"
                        type="password"
                        placeholder={t.newPasswordPlaceholder || "Enter new password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        disabled={loading}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t.confirmPassword || "Confirm Password"}</label>
                    <div className="form-input-wrap">
                      <span className="form-input-icon">🔒</span>
                      <input
                        className="form-input"
                        type="password"
                        placeholder={t.confirmPasswordPlaceholder || "Confirm new password"}
                        value={newPassword2}
                        onChange={(e) => setNewPassword2(e.target.value)}
                        disabled={loading}
                      />
                    </div>
                  </div>
                  <button
                    className="btn-primary"
                    type="submit"
                    disabled={loading}
                    style={{ width: "100%" }}
                  >
                    {loading ? "…" : t.resetPasswordBtn || "Reset Password"}
                  </button>
                </form>
              </>
            ) : (
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
                <h2 style={{ fontSize: 20, marginBottom: 8, color: "var(--dark)" }}>
                  {t.passwordResetSuccess || "Password Reset!"}
                </h2>
                <p style={{ color: "var(--muted)", fontSize: 14, marginBottom: 24 }}>
                  {t.passwordResetSuccessDesc || "Your password has been reset successfully. You can now login with your new password."}
                </p>
                <button className="btn-primary" onClick={onGoToLogin}>
                  {t.goToLogin || "Go to Login"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}